import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, KeyRound, Trash2, Loader2 } from "lucide-react";
import { logger } from "@/lib/logger";

const SOURCE_LABELS = {
  client_own: "Client's own SendGrid account",
  client_subuser: "Master · subuser",
  iceberg_subuser: "Master · Iceberg fallback",
  env_fallback: "Env fallback (legacy)",
  unconfigured: "Not configured",
};

export default function ClientSendGridSection({ clientId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState("");
  const [fromDraft, setFromDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/sendgrid`);
      setState(data);
      setFromDraft(data.own_from_email || "");
    } catch (e) {
      // non-fatal
      logger.warn("ClientSendGridSection: load failed", e);
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const saveOwn = async () => {
    if (!keyDraft.trim() || !fromDraft.trim()) {
      toast.error("API key and From email required");
      return;
    }
    try {
      await api.put(`/clients/${clientId}/sendgrid/own`,
        { api_key: keyDraft.trim(), from_email: fromDraft.trim() });
      toast.success("Saved");
      setKeyDraft("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const clearOwn = async () => {
    if (!window.confirm("Remove this client's own SendGrid account?")) return;
    try {
      await api.delete(`/clients/${clientId}/sendgrid/own`);
      toast.success("Cleared"); setKeyDraft(""); setFromDraft("");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading SendGrid settings…
        </CardContent>
      </Card>
    );
  }
  if (!state) return null;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="client-sendgrid-section">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="label-overline flex items-center gap-1">
            <Mail className="h-3 w-3" /> SendGrid sender
          </div>
          <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
            {SOURCE_LABELS[state.resolved?.source] || state.resolved?.source}
          </Badge>
        </div>

        {/* Resolution summary */}
        <div className="rounded-sm bg-zinc-50/60 border border-zinc-200 p-3 text-xs text-zinc-700 space-y-0.5">
          <div><strong className="text-zinc-900">Current sender:</strong> {state.resolved?.from_email || "(none)"}</div>
          {state.resolved?.on_behalf_of && (
            <div>
              <strong className="text-zinc-900">Subuser scope:</strong>{" "}
              <code className="font-mono">{state.resolved.on_behalf_of}</code>
            </div>
          )}
          {state.subuser_username && (
            <div className="text-emerald-700">
              ✓ Assigned subuser: <code className="font-mono">{state.subuser_username}</code> (managed from the master SendGrid page)
            </div>
          )}
        </div>

        {/* Own key override */}
        <div className="mt-5 border-t border-zinc-100 pt-4">
          <div className="label-overline mb-2 flex items-center gap-1">
            <KeyRound className="h-3 w-3" /> Override — client&apos;s own SendGrid account
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            If this client has their own separate SendGrid account (outside the master),
            paste their API key + verified sender here. When set, this <strong>wins</strong>
            over any assigned subuser.
          </p>
          {state.own_api_key_present && (
            <div className="text-[11px] font-mono text-emerald-700 mb-2">
              ✓ Own key on file (ending …{state.own_api_key_tail})
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label className="label-overline mb-1.5">API key</Label>
              <Input value={keyDraft} type="password"
                onChange={(e) => setKeyDraft(e.target.value)}
                placeholder="SG.xxxx…"
                className="rounded-sm font-mono"
                data-testid="client-sg-own-key-input" />
            </div>
            <div>
              <Label className="label-overline mb-1.5">Verified From email</Label>
              <Input value={fromDraft}
                onChange={(e) => setFromDraft(e.target.value)}
                placeholder="reports@client.com"
                className="rounded-sm"
                data-testid="client-sg-own-from-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveOwn}
                className="rounded-sm bg-[#002FA7] text-white hover:bg-[#00227A]"
                data-testid="client-sg-own-save"
                disabled={!keyDraft.trim() || !fromDraft.trim()}>
                Save
              </Button>
              {state.own_api_key_present && (
                <Button variant="outline" onClick={clearOwn}
                  className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="client-sg-own-clear">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
